import { ownLaag } from '@eformulieren/engine'
import type { Side, Laag, Role, ViewMode } from '@eformulieren/model'

/**
 * Wat de pagina van de instantie en van de kijker weet, en meer heeft de afleiding niet nodig.
 *
 * `actieveLaag`, `rol`, `kant`, `magIndienen` en `beheer` staan in de projectie; `status` en
 * `heeftVoorganger` komen als aparte sleutel naast de projectie mee op `GET /projectie`
 * (`server.ts`). Alles komt dus uit het
 * ene verzoek dat de pagina toch al doet, en dat verzoek is fataal: mislukt het, dan komt de
 * pagina niet op, en dan is er ook geen gedaante die stil op `invullen` terugvalt.
 */
export interface AppearanceBasis {
  readonly status: 'open' | 'bevroren'
  readonly actieveLaag: Laag
  readonly rol: Role
  readonly kant: Side
  /** Of de kijker als invuller van het processysteem is toegekend (R-69); zie `ownLaag`. */
  readonly magIndienen: boolean
  /** Of de kijker alleen als functioneel beheerder kijkt (R-155f). */
  readonly beheer: boolean
  readonly heeftVoorganger: boolean
}

/**
 * De weergavemodus van deze pagina, voor deze kijker op deze instantie (R-138a, D-25, 8.17).
 *
 * **De gedaante volgt uit de instantie.** Sinds M9 is elke beurt een eigen instantie: het
 * processysteem maakt voor de toets een opvolger met `actieveLaag: 'toets'`, een afgesloten beurt
 * is bevroren, en een teruggelegde aanvraag krijgt een opvolger in de invoerlaag
 * (`docs/koppelvlak.md` hoofdstuk 4). Het processysteem kiest de gedaante dus al door de instantie
 * aan te maken; deze functie leest hem. Vóór M9 stond hier dat afleiden "beleid van het
 * processysteem" was, en toen was dat zo: één instantie liep door alle beurten heen.
 *
 * **De gedaante is geen recht.** De pod bewaakt elke schrijfactie zelf (`overdracht.ts`,
 * `oordeel.ts`, de samenwerklaag); een verkeerde gedaante geeft hooguit een knop die de pod
 * weigert, nooit een schrijfactie die er niet hoort.
 *
 * De regels, in volgorde, en de eerste die past wint:
 *
 * 1. Een expliciete modus (uit `window.__proef` of `sessionStorage`) wint altijd. Het voorbeeld
 *    van de configurator (D-61) en de e2e-flows zetten hem zo.
 * 2. Bevroren, of een opening door beheer: `nalezen` (R-133, R-86, R-192, R-155f). Een gesloten
 *    beurt is alleen-lezen, voor iedereen, en een beheerder schrijft nergens.
 * 3. De laag waarin de kijker schrijft is niet de laag die aan zet is: `nalezen` (R-172b, R-193,
 *    R-196). Een invuller in de toetsbeurt ziet zijn invoer alleen-lezen, en een toetser schrijft
 *    nooit in de invoerlaag. Eén regel voor beide kanten, met `ownLaag` uit de engine: wie als
 *    invuller én als toetser is toegekend schrijft in de laag die aan zet is (R-138).
 * 4. De toetsbeurt, voor de toetser: `toetsen` (R-189).
 * 5. De invoerbeurt met een voorganger: `aanvullen` (R-132, R-176). Alleen terugleggen levert een
 *    opvolger in de invoerlaag (de tabel in koppelvlak hoofdstuk 4), dus dit is de beurt na een
 *    teruglegging.
 * 6. Een invuller van de interne kant: `intern` (R-138, R-170). Dat is de gedaante met de
 *    markering extern-zichtbaar per veld, en R-170 vraagt die voor wie het formulier intern
 *    gebruikt. Na `aanvullen` en niet ervoor: in een aanvulbeurt is de werkverzameling de
 *    opdracht, en een gedaante die beide toont is er niet.
 * 7. Anders: `invullen` (R-131).
 *
 * `geanonimiseerd` en `met-historie` volgen nooit uit de instantie: de eerste is de projectie
 * zelf (R-135, R-01a), de tweede is een keuze van de lezer bij een terugkerende uitvraag (ontwerp
 * 6.6) en geen gevolg van de beurt: de instantie zegt niet of iemand de eerdere antwoorden bij de
 * vraag wil zien (R-137). Die blijven expliciet.
 */
export function appearanceOf(appearanceBasis: AppearanceBasis, explicit?: ViewMode): ViewMode {
  if (explicit !== undefined) return explicit
  if (appearanceBasis.status === 'bevroren' || appearanceBasis.beheer) return 'nalezen'
  if (ownLaag(appearanceBasis) !== appearanceBasis.actieveLaag) return 'nalezen'
  if (appearanceBasis.actieveLaag === 'toets') return 'toetsen'
  if (appearanceBasis.heeftVoorganger) return 'aanvullen'
  if (appearanceBasis.kant === 'intern') return 'intern'
  return 'invullen'
}
